<script lang="ts" setup>
import { computed } from 'vue'
import InputLabel from '@/components/input/InputLabel.vue'
import InputErrors from '@/components/input/InputErrors.vue'
import type { SizeKey } from '@/types/tailwind'
import { widthSize } from '@/globals/tailwind'

// props & emit
const props = withDefaults(
  defineProps<{
    placeholder?: string
    modelValue: string | number | undefined | null
    type?: string
    name: string
    label?: string
    optional?: boolean
    loading?: boolean
    disabled?: boolean
    min?: number
    max?: number
    errors?: string[] | null
    size?: SizeKey
  }>(),
  {
    placeholder: 'Type something...',
    modelValue: '',
    type: 'text',
    label: '',
    optional: false,
    loading: false,
    disabled: false,
    min: 0,
    errors: null,
    size: 'full',
  },
)

const emit = defineEmits(['update:modelValue', 'changeValue'])

const inputValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

function onInput(e: Event) {
  const target = e.target as HTMLInputElement
  const value = props.type === 'number' ? Number(target.value) : target.value
  emit('update:modelValue', value)
}

const onChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  const value = props.type === 'number' ? Number(target.value) : target.value
  emit('changeValue', value)
}
</script>

<template>
  <div class="relative w-full">
    <input-label v-if="label" :label="label" :optional="optional"></input-label>
    <div :class="widthSize[size]" class="relative">
      <input
        :type="type"
        :placeholder="placeholder"
        :value="inputValue"
        :name="name"
        :autocomplete="name"
        @input="onInput"
        @change="onChange"
        :disabled="disabled || loading"
        :min="type === 'number' ? min : undefined"
        :max="type === 'number' ? max : undefined"
        :class="[
          loading || disabled ? 'opacity-50 cursor-not-allowed' : '',
          errors
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500/25'
            : 'border-stone-300 dark:border-stone-700 focus:border-blue-500 focus:ring-blue-500/25',
        ]"
        class="w-full px-4 py-3 bg-stone-100 dark:bg-stone-900 border rounded-xl focus:outline-none focus:ring-4 placeholder:text-stone-500/50 placeholder:font-sans transition-colors duration-150"
      />
      <span
        v-if="loading"
        class="absolute top-1/2 -translate-y-1/2 right-4 animate-spin w-4 h-4 border-2 border-black dark:border-white border-t-transparent dark:border-t-transparent rounded-full"
      ></span>
    </div>
    <slot></slot>
    <input-errors v-if="errors" :errors="errors"></input-errors>
  </div>
</template>
